import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  withStyles,
  withMobileDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  FormControl,
  Chip,
  Icon,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import { UserService } from '@pingum/api-services';
import { addEvent } from '@pingum/app-state-management/event/actions';
import { addEventFromTemplate } from '@pingum/app-state-management/eventTemplate/actions';

import { getTenantFromUrl } from '../../../../../utils/urlHelper';
import { getEventDataValidationText } from '../../../../../utils/eventDataHelper';
import { isUserAdmin } from '../../../../../utils/tenantAuthenticationHelper';

import EventTemplateSearchList from '../EventTemplateSearchList';
import FieldDisplayMessage from '../../../FieldDisplayMessage';
import EditEventDueDate from '../../../EditEventDueDate';
import TagDownshift from '../../../../forms/TagDownshift';
import StatusAssignment from '../../../StatusAssignment';
import EventDataForm from '../../../../pages/EventDataSettings/components/EventDataForm';
import EventDataSelector from '../../../../forms/EventDataSelector';

import styles from './styles';

const defaultStartStatus = {
  id: 0,
  name: 'Select a value...',
  icon: 'search',
  iconColor: 'gray',
};
const getEmptyEvent = () => ({
  name: '',
  description: '',
  eventData: [],
  tags: [],
  defaultStartStatus,
});

const getDefaultStateForProps = (eventTemplate) => ({
  eventTemplate,
  showEventForm: !!eventTemplate && !!eventTemplate.id,
  selectedEventsTab: 0,
  isTemplateSelectorOpen: false,
  recentEvents: window.localStorage.getItem(`recentEvents-${getTenantFromUrl()}`) === null
    ? []
    : JSON.parse(window.localStorage.getItem(`recentEvents-${getTenantFromUrl()}`)).reverse(),
  name: eventTemplate.id
    ? eventTemplate.name
    : getEmptyEvent().name,
  startingStatus: eventTemplate.id
    ? eventTemplate.defaultStartStatus
    : getEmptyEvent().defaultStartStatus,
  tags: eventTemplate.id
    ? eventTemplate.tags
    : getEmptyEvent().tags,
  tagInputValue: '',
  description: eventTemplate.id
    ? eventTemplate.description
    : getEmptyEvent().description,
  eventData: eventTemplate.id
    ? eventTemplate.eventData.map((ed) => ({
      ...ed,
      value: eventTemplate.eventDataDefaultValues[ed.id] || '',
    }))
    : getEmptyEvent().eventData,
  dueDate: null,
  assignment: null,
  showDueDatePicker: false,
  eventDataInputValue: '',
  workflowStatus: null,
  validation: {
    name: {
      hasChanged: eventTemplate.id,
      isValid: true,
      message: 'Please provide a valid name (between 2 and 50 characters in length).',
    },
    startingStatus: {
      isValid: true,
      message: 'You must select a starting status for the event.',
    },
    description: {
      isValid: true,
      message: 'Please provide a valid description (between 2 and 1024 characters in length).',
    },
    dueDate: {
      isValid: true,
      message: 'Due date must be set for the future.',
    },
    assignment: { isValid: true },
  },
});

export class CreateEventModalComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = getDefaultStateForProps(this.props.eventTemplate);
  }

  handleNameChange = (e) => {
    const changedValue = e.target.value;
    const isValid
      = changedValue !== null
        && changedValue !== undefined
        && changedValue.length >= 2
        && changedValue.length <= 50;
    this.setState((prevState) => ({
      name: changedValue,
      validation: {
        ...prevState.validation,
        name: {
          hasChanged: true,
          isValid,
          message: prevState.validation.name.message,
        },
      },
    }));
  }

  handleStartingStatusChange = (startingStatus) => {
    if (startingStatus.id === this.state.startingStatus.id) {
      return;
    }
    this.setState((prevState) => ({
      startingStatus,
      validation: {
        ...prevState.validation,
        startingStatus: {
          isValid: true,
          message: '',
        },
      },
    }));
  }

  handleWorkflowStatusChange = (workflowStatus) => {
    this.setState((prevState) => ({
      workflowStatus,
      validation: {
        ...prevState.validation,
        assignment: { isValid: true },
      },
    }));
  }

  handleAssignmentChange = (user) => {
    let { assignment: updatedUserData } = this.state;
    updatedUserData = updatedUserData || '';
    const userInList = updatedUserData.indexOf(user) >= 0;
    if (userInList) {
      updatedUserData = updatedUserData.replace(user, '');
    } else {
      updatedUserData = updatedUserData.concat(`@[${user.userFullName}](${user.auth0UserId})`);
    }

    this.setState((prevState) => ({
      assignment: updatedUserData,
      validation: {
        ...prevState.validation,
        assignment: { isValid: !prevState.workflowStatus || !prevState.workflowStatus.requiredAssignment || !!updatedUserData },
      },
    }));
  }

  handleChangeTag = (selectedItem) => {
    if (this.state.tags.filter((tag) => tag.id === selectedItem.id).length > 0) {
      this.removeSelectedTagItem(selectedItem);
    } else {
      this.addSelectedTagItem(selectedItem);
    }
  }

  addSelectedTagItem = (item) => {
    this.setState((prevState) => ({
      tags: prevState.tags.concat(item),
      tagInputValue: '',
    }));
  }

  removeSelectedTagItem = (item) => {
    this.setState((prevState) => ({
      tags: prevState.tags.filter((tag) => tag.id !== item.id),
      tagInputValue: '',
    }));
  }

  handleChangeTagInput = (inputVal) => {
    this.setState({ tagInputValue: inputVal || '' });
  }

  handleDescriptionChange = (e) => {
    const changedValue = e.target.value;
    const isValid
      = !!changedValue
          && changedValue.length <= 1024;
    this.setState((prevState) => ({
      description: changedValue,
      validation: {
        ...prevState.validation,
        description: {
          isValid,
          message: prevState.validation.description.message,
        },
      },
    }));
  }

  handleChangeDueDate = (changedValue) => {
    const isValid
      = !!changedValue
          && changedValue.getTime() > new Date().getTime();
    this.setState((prevState) => ({
      dueDate: changedValue,
      validation: {
        ...prevState.validation,
        dueDate: {
          isValid,
          message: prevState.validation.dueDate.message,
        },
      },
    }));
  }

  handleUpdatedEventDataInput = (inputVal) => {
    this.setState({ eventDataInputValue: inputVal || '' });
  }

  checkFieldsValid = () => {
    let validFields = true;

    Object.keys(this.state.validation).forEach((key) => {
      if (!(this.state.validation[key] || {}).isValid) {
        validFields = false;
      }
    });

    if (!this.state.validation.name.hasChanged) {
      validFields = false;
      this.setState((prevState) => ({
        validation: {
          ...prevState.validation,
          name: {
            hasChanged: false,
            isValid: false,
            message: prevState.validation.name.message,
          },
        },
      }));
    }
    if (!this.state.startingStatus.id) {
      validFields = false;
      this.setState((prevState) => ({
        validation: {
          ...prevState.validation,
          startingStatus: {
            hasChanged: false,
            isValid: false,
            message: prevState.validation.startingStatus.message,
          },
        },
      }));
    }
    if (!this.state.validation.description.isValid) {
      validFields = false;
    }
    if (!this.checkEventDataValid(this.state.eventData)) {
      validFields = false;
    }
    if (!this.state.validation.dueDate.isValid) {
      validFields = false;
    }
    if (!!this.state.workflowStatus && this.state.workflowStatus.requiredAssignment && !this.state.assignment) {
      validFields = false;
      this.setState((prevState) => ({
        validation: {
          ...prevState.validation,
          assignment: { isValid: false },
        },
      }));
    }

    return validFields;
  }

  submitEvent = (eventTemplateId) => {
    const request = {
      description: this.state.description,
      tags: this.state.tags.map((tag) => tag.id),
      eventData: {},
      dueDate: this.state.dueDate,
      assignment: this.state.assignment,
    };

    if (this.state.eventTemplate.id && this.state.eventTemplate.configurableName) {
      request.name = this.state.name;
    }
    if (this.state.eventTemplate.id && this.state.eventTemplate.configurableStartStatus) {
      request.startingStatusId = this.state.startingStatus.id;
    }

    for (let i = 0; i < this.state.eventData.length; i += 1) {
      request.eventData[this.state.eventData[i].name] = {
        type: this.state.eventData[i].type,
        value: this.state.eventData[i].value
          ? this.state.eventData[i].value
          : '',
      };
    }

    if (!this.checkFieldsValid()) {
      return;
    }

    if (eventTemplateId) {
      this.props.addEventFromTemplate(eventTemplateId, request);
    } else {
      request.name = this.state.name;
      request.startingStatusId = this.state.startingStatus.id;

      this.props.addEvent(request);
    }

    this.handleResetForm();
    this.props.toggleCreateEventModal();
  }

  handleRecentEventClick = (recentEvent) => {
    this.setState((prevState) => ({
      ...recentEvent,
      validation: {
        ...prevState.validation,
        ...recentEvent.validation,
      },
    }));
  }

  handleResetForm = () => {
    this.setState(getDefaultStateForProps(this.props.eventTemplate));
  }

  handleUpdatedEventData = (updatedEventData) => {
    this.setState(() => ({
      eventData: updatedEventData,
      eventDataInputValue: '',
    }));
  }

  checkEventDataValid = (eventData) => {
    const updatedEventData = eventData;
    let isValid = true;
    for (let i = 0; i < updatedEventData.length; i += 1) {
      const isValidResponse = getEventDataValidationText(updatedEventData[i], updatedEventData[i].required);
      updatedEventData[i].error = !!isValidResponse;
      updatedEventData[i].helperText = isValidResponse;
      if (isValidResponse) {
        isValid = false;
      }
    }
    this.setState({ eventData: updatedEventData });

    return isValid;
  }

  render() {
    const {
      classes,
      user,
      tenant,
      fullScreen,
      statuses,
    } = this.props;

    const { showEventForm } = this.state;

    const roles = UserService.getUserRolesForTenant(user, tenant);
    const isAdmin = isUserAdmin(roles);

    const eventForm = (
      <form>
        <FormControl
          fullWidth
          className={classes.formControl}
        >
          <TextField
            error={!this.state.validation.name.isValid}
            required
            variant="outlined"
            id="name"
            label="Name"
            value={this.state.name}
            autoFocus
            autoComplete="off"
            onChange={this.handleNameChange}
            disabled={
              this.state.eventTemplate.id !== undefined
                && !this.state.eventTemplate.configurableName
            }
            helperText={(
              <span>
                {
                  !this.state.validation.name.isValid
                    && this.state.validation.name.message
                }
                {
                  this.state.eventTemplate.id !== undefined
                    && !this.state.eventTemplate.configurableName
                    && <span>Selected Workflow does not allow configurable event name</span>
                }
              </span>
            )}
          />
        </FormControl>

        <TextField
          error={!this.state.validation.description.isValid}
          fullWidth
          multiline
          rows="2"
          rowsMax="4"
          variant="outlined"
          id="Description"
          label="Description"
          value={this.state.description}
          onChange={this.handleDescriptionChange}
          className={classes.formControl}
          helperText={
            !this.state.validation.description.isValid
              ? this.state.validation.description.message
              : ''
          }
        />

        {
          statuses.length > 0
            ? (
              <StatusAssignment
                showCurrentStatusBanner={false}
                showStatusCommentField={false}
                selectedStatus={this.state.startingStatus}
                assignment={this.state.assignment}
                event={{ eventTemplateId: this.state.eventTemplate.id }}
                isValidStatus={this.state.validation.startingStatus.isValid}
                isValidAssignment={this.state.validation.assignment.isValid}
                handleSelectedStatusChange={this.handleStartingStatusChange}
                handleWorkflowStatusChange={this.handleWorkflowStatusChange}
                handleAssignmentChange={this.handleAssignmentChange}
                workflowStatuses={this.state.eventTemplate.workflowStatuses}
              />
            )
            : (
              <FormControl
                error={!this.state.validation.startingStatus.isValid}
                fullWidth
                required
                className={classes.formControl}
                variant="outlined"
              >
                <FieldDisplayMessage
                  title="No Statuses defined for your organization"
                  subTitle={
                  isAdmin
                    ? <Link to="/settings/events?tab=0" href="/settings/events?tab=0">Create statuses now</Link>
                    : 'Ask your organization administrator to create statuses from the Event Settings page'
                }
                  icon="info_outlined"
                  disableGutters
                />
              </FormControl>
            )
        }

        <FormControl
          className={classes.formControl}
          fullWidth
        >
          <EditEventDueDate
            dueDate={(this.state.dueDate)
              ? new Date(this.state.dueDate)
              : null}
            onUpdate={this.handleChangeDueDate}
            label="Due Date"
            helperText={
            !this.state.validation.dueDate.isValid
              ? this.state.validation.dueDate.message
              : ''
          }
          />
        </FormControl>

        <FormControl
          fullWidth
          className={classes.formControlDownshift}
        >
          <EventDataForm
            allEventData={this.props.eventData}
            selectedEventData={this.state.eventData}
            eventDataInputValue={this.state.eventDataInputValue}
            handleUpdatedEventData={this.handleUpdatedEventData}
            handleUpdatedEventDataInput={this.handleUpdatedEventDataInput}
            isAdmin={isAdmin}
          />
        </FormControl>

        <Accordion className={classes.accordian}>
          <AccordionSummary
            expandIcon={<Icon>expand_more</Icon>}
            aria-controls="create-event-advanced"
            id="create-event-advanced"
          >
            <Typography>Advanced</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accordianDetails}>
            <FormControl
              fullWidth
              className={classes.formControlDownshift}
            >
              <TagDownshift
                onInputValueChange={this.handleChangeTagInput}
                inputValue={this.state.tagInputValue}
                availableItems={this.props.tags}
                selectedItem={this.state.tags}
                onChange={this.handleChangeTag}
                onRemoveItem={this.removeSelectedTagItem}
                itemToString={(tag) => tag.name}
                tagTypes={this.props.tagTypes}
                readonly={this.state.eventTemplate.id !== undefined
                  && !this.state.eventTemplate.configurableTags}
                isAdmin={isAdmin}
                helperText={(this.state.eventTemplate.id !== undefined
                  && !this.state.eventTemplate.configurableTags)

                  ? 'Tags cannot be modifield for this Workflow'
                  : ''}
              />
            </FormControl>
            <FormControl
              fullWidth
              className={classes.formControlDownshift}
            >
              <EventDataSelector
                onInputValueChange={this.handleUpdatedEventDataInput}
                inputValue={this.state.eventDataInputValue}
                availableItems={this.props.eventData}
                selectedItem={this.state.eventData}
                onChange={(selectedItem) => {
                  this.setState({ eventData: selectedItem });
                }}
                itemToString={(eventData) => eventData.name}
                eventData={this.props.eventData}
                isAdmin={isAdmin}
              />
            </FormControl>
          </AccordionDetails>
        </Accordion>
      </form>
    );

    const eventTemplateSelector = (
      <EventTemplateSearchList
        onSelectEventTemplate={(eventTemplate) => {
          if (!eventTemplate) {
            return this.setState({ showEventForm: true });
          }
          return this.setState(getDefaultStateForProps(eventTemplate));
        }}
        statuses={statuses}
      />
    );

    return (
      <div>
        <Dialog
          open={this.props.isOpen}
          onClose={this.props.toggleCreateEventModal}
          aria-labelledby="form-dialog-title"
          maxWidth="sm"
          fullWidth
          fullScreen={fullScreen}
        >
          <DialogTitle id="form-dialog-title">
            Start Workflow
            {
              this.state.eventTemplate.id
                && (
                  <Chip
                    avatar={(
                      <Avatar>
                        <Icon>
                          info
                        </Icon>
                      </Avatar>
                    )}
                    label={`Workflow: ${this.state.eventTemplate.name}`}
                    style={{ float: 'right' }}
                  />
                )
            }
          </DialogTitle>
          <DialogContent className={classes.container}>
            {
              showEventForm
                ? eventForm
                : eventTemplateSelector
            }
          </DialogContent>
          <DialogActions>
            {
              showEventForm
                && (
                  <Button
                    id="clear-create-event-modal-button"
                    onClick={this.handleResetForm}
                  >
                    Clear
                  </Button>
                )
            }
            <Button
              id="close-create-event-modal-button"
              onClick={this.props.toggleCreateEventModal}
              variant="contained"
            >
              Cancel
            </Button>
            {
              showEventForm
                && (
                  <Button
                    id="create-event-modal-button"
                    onClick={() => this.submitEvent(this.state.eventTemplate.id)}
                    color="primary"
                    variant="contained"
                    disabled={false}
                  >
                    Submit
                  </Button>
                )
            }
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

CreateEventModalComponent.propTypes = {
  classes: PropTypes.object,
  isOpen: PropTypes.bool,
  user: PropTypes.object,
  tenant: PropTypes.string,
  toggleCreateEventModal: PropTypes.func,
  eventTemplate: PropTypes.object,
  statuses: PropTypes.array,
  eventData: PropTypes.array,
  tags: PropTypes.array,
  tagTypes: PropTypes.array,
  addEvent: PropTypes.func,
  addEventFromTemplate: PropTypes.func,
  fullScreen: PropTypes.bool,
};

CreateEventModalComponent.defaultProps = {
  classes: {},
  isOpen: false,
  user: { user_id: '' },
  tenant: undefined,
  toggleCreateEventModal: () => {},
  eventTemplate: {},
  statuses: [],
  eventData: [],
  tags: [],
  tagTypes: [],
  addEvent: () => {},
  addEventFromTemplate: () => {},
  fullScreen: false,
};

export const mapStateToProps = (state) => ({
  user: state.user.user,
  tenant: getTenantFromUrl(),
  eventTemplate: state.eventTemplate.selectedEventTemplate,
  statuses: state.status.statuses,
  eventData: state.eventData.eventData,
  tags: state.tag.tags,
  tagTypes: state.tagType.tagTypes,
});

export const mapDispatchToProps = (dispatch) => ({
  addEvent: (request) => dispatch(addEvent(request)),
  addEventFromTemplate: (eventTemplateId, request) => dispatch(addEventFromTemplate(eventTemplateId, request)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(withMobileDialog({ breakpoint: 'xs' })(CreateEventModalComponent)));
